/**
 * The shared TanStack Query client.
 *
 * Cache policy here is deliberately aggressive. Canvas is slow, and the whole
 * "this feels good" payoff of the project comes from serving a cached page
 * instantly and revalidating behind it (BUILD_PROMPT phase 7). Because Canvas
 * is the single source of truth and any local cache is disposable
 * (brainstorm.md 1.1), being briefly stale costs nothing — a refetch always
 * wins over the cache, never the other way round.
 */
import { QueryClient } from '@tanstack/react-query';

import { CanvasHttpError, CanvasNoTokenError, LiveWriteBlockedError } from './canvas/errors';

const MINUTE = 60 * 1000;

/**
 * Retry only what is worth retrying.
 *
 * A 401 means the token is bad and three more attempts will not fix it — they
 * will just delay the "add a token in Settings" message the user needs to see.
 * A 404 is final. The write guard firing is never retryable and must surface
 * immediately.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof LiveWriteBlockedError) return false;
  if (error instanceof CanvasNoTokenError) return false;
  if (error instanceof CanvasHttpError && !error.isRetryable) return false;
  return failureCount < 2;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Individual hooks in canvas/queries.ts set their own staleTime; this
        // is the floor for anything that does not.
        staleTime: 60 * 1000,
        // Kept long so returning to a page after a detour is instant, and so
        // the phase-7 IndexedDB persistence has something to persist.
        gcTime: 24 * 60 * MINUTE,
        retry: shouldRetry,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
        refetchOnWindowFocus: true,
        // School wifi drops constantly; refetching on reconnect is most of the
        // offline story.
        refetchOnReconnect: true,
        refetchOnMount: false,
      },
      mutations: {
        // Writes are never retried automatically. A duplicate submission is
        // worse than a failed one, and phase 7's write queue handles retry
        // deliberately rather than by accident.
        retry: false,
      },
    },
  });
}

export const queryClient = createQueryClient();
